// src/components/SEO.js
// Renders once, inside <BrowserRouter>. Reads the current route from
// react-router and looks it up in src/data/seo-config.js, then injects:
//   - <title> and meta description
//   - canonical <link>
//   - Open Graph / Twitter tags
//   - JSON-LD: sitewide Organization + LocalBusiness, plus a page-specific
//     Service or LocalBusiness block depending on the route type.
//
// This means every page gets correct SEO automatically — no per-page edits
// needed. To override a specific page, pass props directly:
//   <SEO title="Custom Title" description="Custom description" />

import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import seoConfig, { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE } from "../data/seo-config";

const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "GeneralContractor",
  "name": SITE_NAME,
  "alternateName": "PR Hospital Designers",
  "url": SITE_URL,
  "logo": `${SITE_URL}/logo512.png`,
  "image": DEFAULT_OG_IMAGE,
  "description":
    "NABH-compliant hospital infrastructure — design, build, and equip, all under one roof.",
  "telephone": "+91-99939-99840",
  "email": "hospitalsdesigners@gmail.com",
  "address": {
    "@type": "PostalAddress",
    "addressRegion": "Telangana",
    "addressCountry": "IN",
  },
  "sameAs": [
    "https://www.facebook.com/people/Hospital-Designers/61577796782877/",
    "https://www.instagram.com/tevahpvtltd/",
    "https://www.youtube.com/@tevahpvtltd",
  ],
  "areaServed": [
    "Hyderabad", "Narsapur", "Sangareddy", "Jangaon", "Mahabubnagar",
    "Indresham", "Bhuvanagiri", "Khammam", "Rajahmundry", "Vijayawada",
  ],
};

function buildPageSchema(pathname, entry) {
  const url = `${SITE_URL}${pathname}`;

  if (entry.type === "service") {
    return {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": entry.title.split("|")[0].trim(),
      "description": entry.description,
      "url": url,
      "provider": { "@type": "GeneralContractor", "name": SITE_NAME, "url": SITE_URL },
      "areaServed": ORG_SCHEMA.areaServed,
    };
  }

  if (entry.type === "city") {
    return {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": `${SITE_NAME} - ${entry.city}`,
      "description": entry.description,
      "url": url,
      "telephone": "+91-99939-99840",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": entry.city,
        "addressRegion": "Telangana",
        "addressCountry": "IN",
      },
      "parentOrganization": { "@type": "GeneralContractor", "name": SITE_NAME, "url": SITE_URL },
    };
  }

  return null;
}

export default function SEO({
  title,
  description,
  keywords,
  image,
  schema,
  noindex
}) {
    const { pathname } = useLocation();
  const entry = seoConfig[pathname] || {};

  const finalTitle = title || entry.title || `${SITE_NAME} | Hospital Design & Build`;
  const finalDescription =
    description || entry.description || "NABH-compliant hospital design, build, and equip services by PR Hospital Designers.";
    const finalKeywords =
  keywords || entry.keywords || "";
  const finalImage = image || DEFAULT_OG_IMAGE;
  const canonical = `${SITE_URL}${pathname === "/" ? "" : pathname}`;
  const pageSchema = schema || (entry.type ? buildPageSchema(pathname, entry) : null);

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <link rel="canonical" href={canonical} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow"} />
      <meta name="author" content="PR Hospital Designers" />
      <meta name="publisher" content="PR Hospital Designers" />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={finalImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />

      {/* Sitewide Organization schema — present on every page */}
      <script type="application/ld+json">{JSON.stringify(ORG_SCHEMA)}</script>

      {/* Page-specific schema (Service or LocalBusiness), if applicable */}
      {pageSchema && (
        <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
      )}
    </Helmet>
  );
}
